import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Rocket, Cpu, Database, Satellite } from "lucide-react";
import HeroSection from "./HeroSection";
import Aos from "aos";

const Project1 = () => {
  useEffect(() => {
    Aos.init({
          duration: 1000,
          once: true,
        });
    window.scrollTo(0, 0);
  }, []);

  const specifications = [
    { label: "Bus Mass", value: "5-6 kg", note: "5-6 kg" },
    { label: "Payload Mass", value: "10-11 kg", note: "10-11 kg" },
    { label: "Payload Volume", value: "97 x 197 x 322 mm³", note: "C97 x 197 x 322 mm³"},
    { label: "Avg. Payload Power", value: "13-40 W", note: "13-40 W" },
    { label: "Power Buses", value: "3.3V, 5V, 6-12V (customizable), Battery raw", note: "3.3V, 5V, 6-12V (customizable), Battery raw" },
    { label: "Telemetry Rate", value: "19.2 kbps", note: "19.2 kbps" },
    { label: "Payload Data Rate", value: "up to 125 Mbps", note: "up to 125 Mbps" }
  ];

  const features = [
    {
      icon: <Satellite className="w-6 h-6" />,
      title: "Inter-Satellite Communication",
      description: "AI-autonomous onboard system enabling high-speed data communication between LEO satellites with intelligent scheduling and antenna pointing."
    },
    {
      icon: <Cpu className="w-6 h-6" />,
      title: "Advanced Processing",
      description: "Onboard AI system for autonomous mission execution based on ground control inputs with efficient RF data collection capabilities."
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Multi-Payload Support",
      description: "Compatible with EO, IR, SAR, and hyperspectral imaging systems, providing versatile observation capabilities."
    },
    {
      icon: <Rocket className="w-6 h-6" />,
      title: "IoT Integration",
      description: "Development of IoT-based Sensor-Control loop utilizing LEO CubeSat backhaul for M2M communication."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <Navbar />

      {/* Hero Section */}
      {/* <div className="bg-customBlue text-white min-h-[20vh] flex items-center ">
        <div className="max-w-6xl mx-auto px-6 w-full">
          <div className="flex flex-col items-center text-center space-y-4">
            
            <h1 className="font-nova-square text-4xl font-thin text-black">
            Project ORION β
            </h1>
            <div className="text-sm breadcrumbs text-black">
              <Link to="/" >Home</Link>
              <span className="mx-2">></span>
              <Link to="/project" >Projects</Link>
              <span className="mx-2">></span>
              <span><Link to="/project/project1" >Project ORION β</Link></span>
            </div>
          </div>
        </div>
      </div> */}

      <HeroSection 
        title="Project ORION β" 
        breadcrumb={
          <>
            <Link to={"/project"}>Projects</Link> {' > '} 
            <Link to={"/project/project1"}>Project ORION β</Link>
          </>
        } 
      />

      <main className="max-w-6xl mx-auto px-6 py-12" data-aos="fade-up">
        {/* Overview Section */}
        <section className="mb-16">
          <h2 className="text-2xl font-bold mb-6 text-gray-900 w-52 relative after:absolute after:bottom-0 after:left-0 after:w-[96%] after:h-1 after:bg-blue-700 after:transform after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300">Project Overview</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="prose max-w-none">
              <p className="text-lg text-gray-700 text-justify">
                Project ORION β represents a significant advancement in autonomous satellite technology, 
                specifically designed for Low Earth Orbit (LEO) operations. The platform integrates 
                AI-driven systems with advanced communication capabilities, enabling autonomous 
                inter-satellite data transfer and efficient mission execution.
              </p>
            </div>
            <div className="prose max-w-none">
              <p className="text-lg text-gray-700 text-justify">
                This innovative system marks a crucial step towards establishing robust space-based 
                infrastructure, supporting applications ranging from Earth observation to advanced 
                IoT connectivity through satellite backhaul networks.
              </p>
            </div>
          </div>
        </section>

        {/* Key Features Grid */}
        <section className="mb-16">
          <h2 className="text-2xl w-40 font-bold mb-6 text-gray-900 relative after:absolute after:bottom-0 after:left-0 after:w-[96%] after:h-1 after:bg-blue-700 after:transform after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300">Key Features</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="bg-lightBlue p-6 rounded-lg shadow-sm border border-gray-100 hover:shadow-lg transition-shadow">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="text-blue-600">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
                </div>
                <p className="text-lg  text-gray-700 text-justify">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Technical Specifications */}
        <section>
          <h2 className="text-2xl font-bold mb-6 text-gray-900 relative after:absolute after:bottom-0 after:left-0 after:w-[75%] after:sm:w-[25%] after:2xl:w-[25%] after:h-1 after:bg-blue-700 after:transform after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-300">Technical Specifications</h2>
          <div className="bg-lightBlue rounded-lg shadow-sm border border-gray-100">
            <div className="grid md:grid-cols-2 gap-4 p-6 hover:shadow-lg transition-shadow">
              {specifications.map((spec, index) => (
                <div key={index} className="flex flex-col hover:shadow-lg transition-shadow px-4 py-2">
                  <div className="flex justify-between items-baseline">
                    <span className="text-lg  text-gray-600">{spec.label}</span>
                    {/* <span className="text-lg text-gray-900 font-medium">{spec.value}</span> */}
                  </div>
                  {spec.note && (
                    <span className="text-lg text-sm text-gray-500">{spec.note}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Project1;